import { supabase } from "@/lib/supabase";

export type ContentStatus =
  | "draft"
  | "editorial_review"
  | "seo_review"
  | "approved"
  | "scheduled"
  | "published"
  | "updated"
  | "archived";

export type ContentBlock = {
  type: "paragraph" | "heading" | "bullet" | "quote" | "callout";
  text: string;
};

export type ContentAuthor = {
  id: string;
  display_name: string;
  role_title: string | null;
  biography: string | null;
  credentials: string[];
};

export type ContentEntry = {
  id: string;
  content_type: string;
  title: string;
  slug: string;
  route_path: string;
  excerpt: string;
  author_id: string | null;
  reviewer_id: string | null;
  hero_image_url: string | null;
  body: ContentBlock[];
  category: string | null;
  tags: string[];
  canonical_url: string | null;
  seo_title: string;
  meta_description: string;
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  robots: "index_follow" | "noindex_follow" | "noindex_nofollow";
  structured_data_type: string;
  cta: Record<string, unknown> | null;
  availability: "available" | "beta" | "coming_soon" | "planned";
  status: ContentStatus;
  scheduled_for: string | null;
  published_at: string | null;
  publication_visible: boolean;
  updated_at: string;
  author?: { display_name: string } | null;
  reviewer?: { display_name: string } | null;
};

export type ContentRevision = {
  id: string;
  revision_number: number;
  change_note: string | null;
  created_at: string;
  snapshot: ContentEntry;
};

export type ContentWorkflowEvent = {
  id: string;
  from_status: ContentStatus | null;
  to_status: ContentStatus;
  reason: string;
  scheduled_for: string | null;
  created_at: string;
};

export type ContentDraft = {
  content_type: string;
  title: string;
  slug: string;
  route_path: string;
  excerpt: string;
  author_id: string;
  reviewer_id: string;
  hero_image_url: string;
  body_text: string;
  category: string;
  tags: string;
  canonical_url: string;
  seo_title: string;
  meta_description: string;
  og_title: string;
  og_description: string;
  og_image_url: string;
  robots: ContentEntry["robots"];
  structured_data_type: string;
  availability: ContentEntry["availability"];
};

function dataOrThrow<T>(data: T | null, error: { message: string } | null) {
  if (error) throw new Error(error.message);
  if (data === null) throw new Error("RAQOZ returned no content data");
  return data;
}

export function bodyTextToBlocks(value: string): ContentBlock[] {
  return value
    .trim()
    .split(/\n{2,}/)
    .flatMap((part): ContentBlock[] => {
      const text = part.trim();
      if (!text) return [];
      if (text.startsWith("## "))
        return [{ type: "heading", text: text.slice(3).trim() }];
      if (text.startsWith("> "))
        return [{ type: "quote", text: text.slice(2).trim() }];
      if (text.startsWith("! "))
        return [{ type: "callout", text: text.slice(2).trim() }];
      const lines = text.split("\n").map((line) => line.trim());
      if (lines.every((line) => line.startsWith("- ")))
        return lines.map((line) => ({
          type: "bullet",
          text: line.slice(2).trim(),
        }));
      return [{ type: "paragraph", text }];
    });
}

export function blocksToBodyText(blocks: ContentBlock[] = []) {
  return blocks
    .map((block) => {
      if (block.type === "heading") return `## ${block.text}`;
      if (block.type === "quote") return `> ${block.text}`;
      if (block.type === "callout") return `! ${block.text}`;
      if (block.type === "bullet") return `- ${block.text}`;
      return block.text;
    })
    .join("\n\n");
}

export async function loadEditorialWorkspace() {
  const due = await supabase.rpc("cms_publish_due_content");
  if (due.error) throw new Error(due.error.message);
  const [entries, authors] = await Promise.all([
    supabase
      .from("content_entries")
      .select(
        "*,author:content_authors!content_entries_author_id_fkey(display_name),reviewer:content_authors!content_entries_reviewer_id_fkey(display_name)",
      )
      .order("updated_at", { ascending: false }),
    supabase
      .from("content_authors")
      .select("*")
      .eq("active", true)
      .order("display_name"),
  ]);
  return {
    entries: dataOrThrow(entries.data, entries.error) as ContentEntry[],
    authors: dataOrThrow(authors.data, authors.error) as ContentAuthor[],
  };
}

export async function loadContentHistory(contentEntryId: string) {
  const [revisions, events] = await Promise.all([
    supabase
      .from("content_revisions")
      .select("id,revision_number,change_note,created_at,snapshot")
      .eq("content_entry_id", contentEntryId)
      .order("revision_number", { ascending: false }),
    supabase
      .from("content_workflow_events")
      .select("id,from_status,to_status,reason,scheduled_for,created_at")
      .eq("content_entry_id", contentEntryId)
      .order("created_at", { ascending: false }),
  ]);
  return {
    revisions: dataOrThrow(revisions.data, revisions.error) as ContentRevision[],
    events: dataOrThrow(events.data, events.error) as ContentWorkflowEvent[],
  };
}

export async function createEditorialAuthor(
  displayName: string,
  roleTitle: string,
) {
  const { data, error } = await supabase.rpc("cms_create_content_author", {
    p_display_name: displayName,
    p_role_title: roleTitle || null,
    p_biography: null,
    p_credentials: [],
  });
  return dataOrThrow(data, error) as ContentAuthor;
}

export async function saveContentDraft(
  contentEntryId: string | null,
  draft: ContentDraft,
  changeNote: string,
) {
  const payload = {
    ...draft,
    body: bodyTextToBlocks(draft.body_text),
    tags: draft.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
  };
  delete (payload as Partial<typeof payload>).body_text;
  const { data, error } = await supabase.rpc("cms_save_content", {
    p_content_entry_id: contentEntryId,
    p_payload: payload,
    p_change_note: changeNote,
  });
  return dataOrThrow(data, error) as ContentEntry;
}

export async function transitionContent(
  contentEntryId: string,
  toStatus: ContentStatus,
  reason: string,
  scheduledFor?: string,
) {
  const { data, error } = await supabase.rpc("cms_transition_content", {
    p_content_entry_id: contentEntryId,
    p_to_status: toStatus,
    p_reason: reason,
    p_scheduled_for: scheduledFor || null,
  });
  return dataOrThrow(data, error) as ContentEntry;
}
